<script setup lang="ts">
import type { Schemas } from "#shopware";

const props = defineProps<{
  addresses: Schemas["CustomerAddress"][];
  currentAddress: Schemas["CustomerAddress"] | null;
  addressType: 'billing' | 'shipping';
  countries: any[];
  salutations: any[];
}>();

const emits = defineEmits<{
  (e: "select", addressId: string): void;
  (e: "addNew"): void;
  (e: "close"): void;
}>();

const selectedAddressId = ref<string>("");

// Filter out the current address from the selection list
const availableAddresses = computed(() => {
  return props.addresses.filter(address => address.id !== props.currentAddress?.id);
});

const handleAddressSelect = () => {
  if (selectedAddressId.value) {
    emits("select", selectedAddressId.value);
  }
};

const handleAddNew = () => {
  emits("addNew");
};
const { t } = useI18n();
</script>

<template>
  <div class="min-h-full flex flex-col p-0 md:p-6">
    <div class="max-w-2xl w-full space-y-6">


      <!-- Header -->
      <div class="flex items-center justify-between">
        <h2 class="text-start text-lg md:text-xl font-medium text-primary">
          {{ $t(`checkout.change${addressType === 'billing' ? 'Billing' : 'Shipping'}Address`) }}
        </h2>
      </div>

      <!-- Address Selection -->
      <div class="space-y-4">
        <p class="text-sm text-gray-600">
          {{ $t(`checkout.select${addressType === 'billing' ? 'Billing' : 'Shipping'}Address`) }}
        </p>

        <!-- Existing Addresses -->
        <div class="space-y-3">
          <div
            v-for="address in availableAddresses"
            :key="address.id"
            class="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors cursor-pointer"
            :class="{
              'border-primary bg-primary/5': selectedAddressId === address.id,
              'border-gray-200': selectedAddressId !== address.id
            }"
            @click="selectedAddressId = address.id"
          >
            <input
              :id="`${addressType}-${address.id}`"
              :value="address.id"
              :checked="selectedAddressId === address.id"
              name="address-selection"
              type="radio"
              class="mt-1 h-4 w-4 border-secondary-300 text-primary focus:ring-primary"
            />
            <label
              :for="`${addressType}-${address.id}`"
              class="flex-1 cursor-pointer"
            >
              <!-- Simple address display instead of CheckoutAddressCard -->
              <div class="address-card">
                <p class="font-medium">{{ address.firstName }} {{ address.lastName }}</p>
                <p class="text-sm text-gray-600">{{ address.street }}</p>
                <p class="text-sm text-gray-600">{{ address.zipcode }} {{ address.city }}</p>
                <p class="text-sm text-gray-600">{{ address.country.name }}</p>
                <p class="text-sm text-gray-600">{{ address.phoneNumber }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- No other addresses message -->
        <div v-if="availableAddresses.length === 0" class="text-center py-8 text-gray-500">
          <p>{{ $t("checkout.noOtherAddress") }}</p>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3 pt-4">
        <button
          v-if="availableAddresses.length > 0"
          type="button"
          class="btn btn-primary w-full md:w-[50%] justify-center flex-shrink-1"
          :disabled="!selectedAddressId"
          @click="handleAddressSelect"
        >
          {{ $t("checkout.selectAddress") }}
        </button>
        
        <button
          type="button"
          class="btn btn-secondary w-full md:w-[50%] justify-center flex-shrink-0"
          @click="handleAddNew"
        >
          {{ $t("checkout.addNewAddress") }}
        </button>
      </div>
    </div>
  </div>
</template>
